import type { CollectionEntityDefinition, SingleEntityDefinition } from '@gzl10/nexus-sdk'
import { useIdField, useTextField, useSelectField, useNumberField, useSwitchField, useDatetimeField, useEmailField, useMetadataField, useExpiresAtField, usePasswordField } from '@gzl10/nexus-sdk/fields'

/**
 * Single Entity: auth_config
 * Authentication configuration persisted in single_records.
 * Env vars override DB values via envMapping.
 */
export const authConfigEntity: SingleEntityDefinition = {
  type: 'single',
  key: 'auth_config',
  label: { en: 'Auth Config', es: 'Configuración de autenticación' },
  routePrefix: '/config',
  timestamps: true,
  order: 10,

  defaults: {
    access_expires: '15m',
    refresh_expires: '7d',
    rate_limit_max: 5,
    rate_limit_window: 900,
    cookie_domain: '',
    challenge_threshold: 2,
    skip_register_otp: false,
    disable_registration: false,
    disable_auto_create: false,
    hibp_check: false,
    hibp_mode: 'block',
    captcha_provider: 'none',
    captcha_site_key: '',
    captcha_secret_key: '',
    captcha_on_login: false,
    captcha_on_register: true,
    captcha_on_forgot_password: true
  },

  envMapping: {
    access_expires: { env: 'AUTH_ACCESS_EXPIRES' },
    refresh_expires: { env: 'AUTH_REFRESH_EXPIRES' },
    rate_limit_max: { env: 'AUTH_RATE_LIMIT_MAX', parse: 'number' },
    rate_limit_window: { env: 'AUTH_RATE_LIMIT_WINDOW', parse: 'number' },
    cookie_domain: { env: 'AUTH_COOKIE_DOMAIN' },
    challenge_threshold: { env: 'AUTH_CHALLENGE_THRESHOLD', parse: 'number' },
    skip_register_otp: { env: 'AUTH_SKIP_REGISTER_OTP', parse: 'boolean' },
    disable_registration: { env: 'AUTH_DISABLE_REGISTRATION', parse: 'boolean' },
    disable_auto_create: { env: 'AUTH_DISABLE_AUTO_CREATE', parse: 'boolean' },
    hibp_check: { env: 'AUTH_HIBP_CHECK', parse: 'boolean' },
    hibp_mode: { env: 'AUTH_HIBP_MODE' },
    captcha_provider: { env: 'AUTH_CAPTCHA_PROVIDER' },
    captcha_site_key: { env: 'AUTH_CAPTCHA_SITE_KEY' },
    captcha_secret_key: { env: 'AUTH_CAPTCHA_SECRET_KEY' },
    captcha_on_login: { env: 'AUTH_CAPTCHA_ON_LOGIN', parse: 'boolean' },
    captcha_on_register: { env: 'AUTH_CAPTCHA_ON_REGISTER', parse: 'boolean' },
    captcha_on_forgot_password: { env: 'AUTH_CAPTCHA_ON_FORGOT_PASSWORD', parse: 'boolean' }
  },

  fields: {
    id: useIdField(),
    access_expires: useTextField({
      label: { en: 'Access Token Expiration', es: 'Expiración del token de acceso' },
      hint: { en: 'e.g. 15m, 1h', es: 'ej: 15m, 1h' },
      size: 20,
      nullable: false
    }),
    refresh_expires: useTextField({
      label: { en: 'Refresh Token Expiration', es: 'Expiración del token de refresco' },
      hint: { en: 'e.g. 7d, 30d', es: 'ej: 7d, 30d' },
      size: 20,
      nullable: false
    }),
    rate_limit_max: useNumberField({
      label: { en: 'Rate Limit (max attempts)', es: 'Límite de intentos' },
      hint: { en: 'Max failed login attempts before lockout', es: 'Máx. intentos fallidos antes del bloqueo' },
      nullable: false
    }),
    rate_limit_window: useNumberField({
      label: { en: 'Rate Limit Window (seconds)', es: 'Ventana de límite (segundos)' },
      hint: { en: 'Window in seconds for rate limiting', es: 'Ventana en segundos para el límite' },
      nullable: false
    }),
    cookie_domain: useTextField({
      label: { en: 'Cookie Domain', es: 'Dominio de cookie' },
      hint: { en: 'For SSO across subdomains (e.g. .example.com)', es: 'Para SSO entre subdominios (ej: .example.com)' },
      size: 255,
      nullable: true
    }),
    challenge_threshold: useNumberField({
      label: { en: 'Challenge Threshold', es: 'Umbral de desafío' },
      hint: { en: 'Failed attempts before requiring OTP', es: 'Intentos fallidos antes de requerir OTP' },
      nullable: false
    }),
    skip_register_otp: useSwitchField({
      label: { en: 'Skip Register OTP', es: 'Omitir OTP en registro' },
      hint: { en: 'Development only — rejected in production', es: 'Solo desarrollo — rechazado en producción' }
    }),
    disable_registration: useSwitchField({
      label: { en: 'Disable Registration', es: 'Deshabilitar registro' },
      hint: { en: 'Disable self-registration via /auth/register', es: 'Deshabilitar auto-registro vía /auth/register' }
    }),
    disable_auto_create: useSwitchField({
      label: { en: 'Disable Auto-Create', es: 'Deshabilitar auto-creación' },
      hint: { en: 'Disable auto-creation on first OIDC login', es: 'Deshabilitar auto-creación en primer login OIDC' }
    }),
    hibp_check: useSwitchField({
      label: { en: 'Leaked Password Check', es: 'Verificar passwords filtrados' },
      hint: { en: 'Check passwords against HaveIBeenPwned breach database. Requires server restart.', es: 'Verificar passwords contra la base de datos de filtraciones HaveIBeenPwned. Requiere reiniciar el servidor.' }
    }),
    hibp_mode: useSelectField({
      label: { en: 'Leaked Password Mode', es: 'Modo de password filtrado' },
      hint: { en: 'Block: reject compromised passwords. Warn: allow but inform user.', es: 'Bloquear: rechazar passwords comprometidos. Avisar: permitir pero informar.' },
      options: [
        { value: 'block', label: { en: 'Block', es: 'Bloquear' } },
        { value: 'warn', label: { en: 'Warn', es: 'Avisar' } }
      ],
      hidden: { field: 'hibp_check', $ne: true }
    }),
    captcha_provider: useSelectField({
      label: { en: 'CAPTCHA Provider', es: 'Proveedor CAPTCHA' },
      hint: { en: 'Turnstile (invisible, recommended) or hCaptcha', es: 'Turnstile (invisible, recomendado) o hCaptcha' },
      options: [
        { value: 'none', label: { en: 'Disabled', es: 'Desactivado' } },
        { value: 'turnstile', label: { en: 'Cloudflare Turnstile', es: 'Cloudflare Turnstile' } },
        { value: 'hcaptcha', label: { en: 'hCaptcha', es: 'hCaptcha' } }
      ]
    }),
    captcha_site_key: useTextField({
      label: { en: 'CAPTCHA Site Key', es: 'Clave de sitio CAPTCHA' },
      hint: { en: 'Public key from your CAPTCHA provider dashboard', es: 'Clave pública del panel de tu proveedor CAPTCHA' },
      size: 255,
      nullable: true,
      hidden: { field: 'captcha_provider', $eq: 'none' }
    }),
    captcha_secret_key: usePasswordField({
      label: { en: 'CAPTCHA Secret Key', es: 'Clave secreta CAPTCHA' },
      hint: { en: 'Server-side secret for verification', es: 'Secreto del servidor para verificación' },
      size: 255,
      nullable: true,
      hidden: { field: 'captcha_provider', $eq: 'none' },
      access: { read: ['ADMIN'] }
    }),
    captcha_on_login: useSwitchField({
      label: { en: 'CAPTCHA on Login', es: 'CAPTCHA en login' },
      hint: { en: 'Require CAPTCHA verification on login', es: 'Requerir verificación CAPTCHA en login' },
      hidden: { field: 'captcha_provider', $eq: 'none' }
    }),
    captcha_on_register: useSwitchField({
      label: { en: 'CAPTCHA on Register', es: 'CAPTCHA en registro' },
      hint: { en: 'Require CAPTCHA verification on registration', es: 'Requerir verificación CAPTCHA en registro' },
      hidden: { field: 'captcha_provider', $eq: 'none' }
    }),
    captcha_on_forgot_password: useSwitchField({
      label: { en: 'CAPTCHA on Forgot Password', es: 'CAPTCHA en olvidé contraseña' },
      hint: { en: 'Require CAPTCHA verification on password reset request', es: 'Requerir verificación CAPTCHA en solicitud de reseteo' },
      hidden: { field: 'captcha_provider', $eq: 'none' }
    })
  },

  casl: {
    subject: 'AuthConfig',
    sensitiveFields: ['captcha_secret_key'],
    permissions: {
      ADMIN: { actions: ['read', 'update'] }
    }
  }
}

/**
 * EntityDefinition for Refresh Tokens.
 *
 * Uses 'temp' type for automatic cleanup of expired tokens.
 * Retention: 7 days - same as jwtRefreshExpires default.
 */
export const refreshTokenEntity: CollectionEntityDefinition = {
  type: 'temp',
  table: 'auth_refresh_tokens',
  label: { en: 'Refresh Token', es: 'Token de refresco' },
  labelPlural: { en: 'Refresh Tokens', es: 'Tokens de refresco' },
  labelField: 'device_name',
  retention: { days: 7, expiresField: 'expires_at' },
  expose: false,

  fields: {
    id: useIdField(),
    token: useTextField({
      label: { en: 'Token', es: 'Token' },
      hidden: true,
      size: 255,
      unique: true,
      nullable: false,
      meta: { exportable: false }
    }),
    user_id: {
      label: { en: 'User', es: 'Usuario' },
      input: 'select',
      required: true,
      db: { type: 'string', size: 26, nullable: false, index: true },
      // Note: No 'relation' - temp entities use Redis/InMemory adapters that don't support FKs
      // The UI dropdown still works via options.endpoint
      options: { endpoint: '/users', valueField: 'id', labelField: 'name' },
      meta: { searchable: true }
    },
    expires_at: useExpiresAtField({
      required: true,
      nullable: false
    }),
    last_used_at: useDatetimeField({
      label: { en: 'Last Used', es: 'Último uso' },
      nullable: true,
      meta: { sortable: true }
    }),
    device_id: useTextField({
      label: { en: 'Device ID', es: 'ID de dispositivo' },
      size: 64,
      index: true,
      nullable: true,
      meta: { searchable: true }
    }),
    device_name: useTextField({
      label: { en: 'Device', es: 'Dispositivo' },
      size: 100,
      nullable: true,
      hint: { en: 'Device readable name (e.g. John\'s iPhone)', es: 'Nombre legible del dispositivo (ej: iPhone de Juan)' }
    }),
    created_at: useDatetimeField({
      label: { en: 'Created', es: 'Creado' },
      disabled: true,
      nullable: true,
      meta: { sortable: true }
    })
  },

  casl: {
    subject: 'AuthRefreshToken',
    permissions: {
      SUPPORT: { actions: ['read'] }
    }
  }
}

/**
 * EntityDefinition for Auth Identities.
 *
 * Centralized linking table between Nexus users and external auth providers
 * (OIDC/OAuth). Plugins use AuthService methods instead of creating their
 * own {provider}_users tables.
 */
export const authIdentitiesEntity: CollectionEntityDefinition = {
  type: 'collection',
  table: 'auth_identities',
  label: { en: 'Auth Identity', es: 'Identidad de autenticación' },
  labelPlural: { en: 'Auth Identities', es: 'Identidades de autenticación' },
  labelField: 'provider',
  timestamps: true,
  hidden: true,
  expose: false,
  order: 5,

  fields: {
    id: useIdField(),
    user_id: useSelectField({
      label: { en: 'User', es: 'Usuario' },
      table: 'users',
      column: 'id',
      onDelete: 'CASCADE',
      required: true,
      index: true,
      endpoint: '/users',
      valueField: 'id',
      labelField: 'name',
      meta: { searchable: true }
    }),
    provider: useTextField({
      label: { en: 'Provider', es: 'Proveedor' },
      required: true,
      size: 50,
      nullable: false,
      disabled: true,
      hint: { en: 'e.g. pocketid, google, microsoft', es: 'ej. pocketid, google, microsoft' },
      meta: { sortable: true, searchable: true }
    }),
    provider_user_id: useTextField({
      label: { en: 'Provider User ID', es: 'ID de usuario del proveedor' },
      required: true,
      size: 255,
      nullable: false,
      disabled: true,
      hint: { en: 'OIDC sub claim or OAuth user ID', es: 'Claim sub OIDC o ID de usuario OAuth' },
      meta: { searchable: true }
    }),
    provider_email: useEmailField({
      label: { en: 'Provider Email', es: 'Email del proveedor' },
      nullable: true,
      disabled: true,
    }),
    metadata: useMetadataField(),
    linked_at: useDatetimeField({
      label: { en: 'Linked At', es: 'Vinculado en' },
      disabled: true,
      nullable: false,
      meta: { sortable: true }
    }),
    last_login_at: useDatetimeField({
      label: { en: 'Last Login', es: 'Último acceso' },
      nullable: true,
      disabled: true,
      meta: { sortable: true }
    }),
  },

  indexes: [
    { columns: ['provider', 'provider_user_id'], unique: true },
    { columns: ['user_id', 'provider'] }
  ],

  casl: {
    subject: 'AuthIdentity',
    permissions: {
      '@': [
        { actions: ['read'], conditions: { user_id: '${user.id}' } }
      ]
    }
  }
}
